import useTranslation from 'next-translate/useTranslation';
import { memo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Box, Grid, InputNumberControl, Modal, ModalProps, Typography } from '@core/component';
import { Translate } from '@core/constant';
import { useContextCurrency, useContextToast, useContextUser } from '@core/context';
import { useLanguage, useProductAlert, useValidator } from '@core/hook';
import {
  CreateProductAlertForm,
  EContactMethod,
  ECountry,
  EFontWeight,
  ETypographyVariant,
  ErrorHandled,
  getPostProductAlertSchema,
} from '@core/type';
import { PostProductAlertEntity } from '@core/type/api';
import { formatCurrency, getInternationalPrefix } from '@core/util';

import { AlertForm } from '../../alert-form';
import { ButtonsModalAddAlert, HeaderModal, ModalSuccessProductAlertUpdate } from './components';
import { ModalAddProductAlertProp } from './interface-modal-alert';

export const ModalAddProductAlert = memo(
  ({ product, isOpen, onCloseModal }: Omit<ModalProps, 'children'> & ModalAddProductAlertProp) => {
    const { phone } = useContextUser();
    const { currency } = useContextCurrency();
    const { addProductAlert } = useProductAlert();
    const { addToast } = useContextToast();
    const [isPhoneCountryModalOpen, setIsPhoneCountryModalOpen] = useState(false);

    const { t: tForm } = useTranslation(Translate.common.FORM);
    const { t: tProduct } = useTranslation(Translate.common.PRODUCT);

    const {
      handleSubmit,
      control,
      trigger,
      reset,
      setError,
      watch,
      setValue,
      formState: { errors, isSubmitSuccessful },
    } = useForm<CreateProductAlertForm>({
      defaultValues: {
        phone: {
          countryIso: phone?.countryIso || ECountry.SWITZERLAND,
          number: phone?.number || '',
        },
        notification: EContactMethod.EMAIL,
      },
      resolver: useValidator(getPostProductAlertSchema(product?.totalPrice), tForm),
      mode: 'onChange',
    });

    const onSubmit = ({ phone: newPhone, ...data }: CreateProductAlertForm) => {
      const submitData: PostProductAlertEntity = {
        ...data,
        productId: product?.id,
        currencyIso: currency,
      };

      if (data.notification === EContactMethod.SMS) {
        if (phone?.code) {
          submitData.phone = phone;
        } else {
          submitData.phone = { ...newPhone, code: getInternationalPrefix(newPhone.countryIso) };
        }
      }

      return addProductAlert({
        submitData,
        product,
      });
    };

    const onError = ({ data, errors, status }: ErrorHandled) => {
      const description = errors?.find((err) => err.attribute === 'product_id')?.message;
      if (description) {
        const { title } = data;
        addToast({ title, status, description });
      }
    };

    const handleModalClose = () => {
      if (!isPhoneCountryModalOpen) {
        onCloseModal();
        reset();
      }
    };

    const formAmount = watch('amount');

    const isPhonePrefilled = !!phone;

    const { language } = useLanguage();

    return (
      <>
        <Modal isOpen={isOpen && !isSubmitSuccessful} onCloseModal={handleModalClose}>
          <HeaderModal title={tProduct('modals.addPriceAlert.title')} subTitle={product?.name} />
          <Modal.Body>
            <Box display="flex" justifyContent="center" marginBottom="32px">
              <Typography variant={ETypographyVariant.BODY2}>
                {tProduct('modals.addPriceAlert.description')}
              </Typography>
            </Box>

            <AlertForm
              formProps={{
                t: tForm,
                trigger,
                reset,
                handleSubmit,
                fn: onSubmit,
                setError,
                onError,
              }}
              phoneInputProps={{
                watch: watch,
                isSubmitted: true,
                control: control,
                setValue: setValue,
                disabled: isPhonePrefilled,
                isPrefilled: isPhonePrefilled,
                trigger: trigger,
                errors: errors,
                state: [isPhoneCountryModalOpen, setIsPhoneCountryModalOpen],
              }}
              actions={<ButtonsModalAddAlert onCancel={handleModalClose} />}
            >
              <Grid>
                <InputNumberControl
                  control={control}
                  name="amount"
                  required
                  label={tProduct('modals.addPriceAlert.yourPrice')}
                  isError={!!errors['amount']}
                  message={
                    (isNaN(formAmount) || formAmount < 1) && tForm('amount.validate.typeError')
                  }
                  fractionDigits={2}
                />
                <Box display="flex" marginTop="8px">
                  <Typography variant={ETypographyVariant.BODY3}>
                    {tProduct('modals.addPriceAlert.currentPrice')}:&nbsp;{' '}
                  </Typography>
                  <Typography variant={ETypographyVariant.BODY3} fontWeight={EFontWeight.BOLD}>
                    {formatCurrency(language, currency, product.totalPrice)}
                  </Typography>
                </Box>
              </Grid>
            </AlertForm>
            <Typography marginTop={'32px'} variant={ETypographyVariant.CAPTION3}>
              {tProduct('stockAlerts.newAlertModal.confirmYourConsent')}
            </Typography>
          </Modal.Body>
        </Modal>
        <ModalSuccessProductAlertUpdate
          name={product?.name}
          isOpen={isOpen && isSubmitSuccessful}
          onCloseModal={handleModalClose}
        />
      </>
    );
  },
);

ModalAddProductAlert.displayName = 'ModalAddProductAlert';
